import apiClient from './api';

export interface LoginRequest {
    email: string;
    password: string;
}

export interface SignupRequest {
    email: string;
    password: string;
    fullName?: string;
    company?: string;
}

export interface AuthResponse {
    success: boolean;
    access_token: string;
    user: {
        id: string;
        email: string;
        fullName: string;
        avatar?: string;
        credits?: number;
    };
}

export interface UserProfile {
    id: string;
    email: string;
    fullName: string;
    company?: string;
    avatar?: string;
    credits: number;
    provider: string;
    createdAt: string;
}

const AUTH_TOKEN_KEY = 'auth_token';
const USER_KEY = 'user';

export const authService = {
    async login(data: LoginRequest): Promise<AuthResponse> {
        const response = await apiClient.post<AuthResponse>('/auth/login', data);
        if (response.data.access_token) {
            this.setToken(response.data.access_token);
            this.setUser(response.data.user);
        }
        return response.data;
    },

    async signup(data: SignupRequest): Promise<AuthResponse> {
        const response = await apiClient.post<AuthResponse>('/auth/signup', data);
        if (response.data.access_token) {
            this.setToken(response.data.access_token);
            this.setUser(response.data.user);
        }
        return response.data;
    },

    async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.post('/auth/forgot-password', { email });
        return response.data;
    },

    async resetPassword(token: string, password: string): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.post('/auth/reset-password', { token, password });
        return response.data;
    },

    async getProfile(): Promise<{ success: boolean; data: UserProfile }> {
        const response = await apiClient.get('/auth/me');
        return response.data;
    },

    logout(): void {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        window.location.href = '/login';
    },

    getToken(): string | null {
        return localStorage.getItem(AUTH_TOKEN_KEY);
    },

    setToken(token: string): void {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
    },

    getUser(): AuthResponse['user'] | null {
        const user = localStorage.getItem(USER_KEY);
        return user ? JSON.parse(user) : null;
    },

    setUser(user: AuthResponse['user']): void {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    },

    isAuthenticated(): boolean {
        return !!this.getToken();
    },

    // OAuth login URLs
    getGoogleLoginUrl(): string {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
        return `${apiUrl}/auth/google`;
    },

    getGithubLoginUrl(): string {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
        return `${apiUrl}/auth/github`;
    },
};

export default authService;

